import React, { useEffect, useMemo, useState } from "react";
import { authFetch, getAuth, isAdmin, isOperator } from "../../auth";
import { ActionModal } from "../Common/ActionModal";
import { usePermission } from "../Auth/PermissionContext";
import { PermissionKeys } from "../../permissions";

function toLocalDateTimeText(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("zh-CN", { hour12: false });
}

function roleName(userType) {
  if (userType === 0) return "Admin";
  if (userType === 1) return "CustomerAdmin";
  if (userType === 2) return "Operator";
  return "Unknown";
}

function toNullableInt(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function UserManagement() {
  const auth = getAuth();
  const admin = isAdmin();
  const operator = isOperator();
  const defaultCustomerId = admin || !auth?.customerId ? "" : String(auth.customerId);
  const { can } = usePermission();
  const canView = can(PermissionKeys.UserView);
  const canCreate = can(PermissionKeys.UserCreate);
  const canUpdate = can(PermissionKeys.UserUpdate);
  const canDelete = can(PermissionKeys.UserDelete);
  const canPasswordReset = can(PermissionKeys.UserPasswordReset);

  const [loginName, setLoginName] = useState("");
  const [realName, setRealName] = useState("");
  const [userType, setUserType] = useState("");
  const [customerId, setCustomerId] = useState(defaultCustomerId);
  const [customers, setCustomers] = useState([]);
  const [customerError, setCustomerError] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [createModal, setCreateModal] = useState({ open: false });
  const [editModal, setEditModal] = useState({ open: false, row: null });
  const [passwordModal, setPasswordModal] = useState({ open: false, row: null, newPassword: "" });
  const [deleteModal, setDeleteModal] = useState({ open: false, row: null });
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  const customerOptions = useMemo(() => {
    const options = admin ? [{ value: "", label: "未绑定客户" }] : [];
    if (!admin && defaultCustomerId && customers.length === 0) {
      options.push({ value: defaultCustomerId, label: `本客户 #${defaultCustomerId}` });
    }
    return [
      ...options,
      ...customers.map((customer) => ({
        value: String(customer.id),
        label: `${customer.name}${customer.code ? ` / ${customer.code}` : ""} #${customer.id}`
      }))
    ];
  }, [admin, customers, defaultCustomerId]);

  const filterCustomerOptions = useMemo(() => {
    const allOption = admin ? [{ value: "", label: "全部客户" }] : [];
    return [...allOption, ...customerOptions.filter((item) => item.value !== "")];
  }, [admin, customerOptions]);

  const roleOptions = useMemo(() => {
    const options = [];
    if (admin) {
      options.push({ value: "0", label: "Admin" });
    }
    options.push({ value: "1", label: "CustomerAdmin" });
    options.push({ value: "2", label: "Operator" });
    return options;
  }, [admin]);

  const createInitialValues = useMemo(() => ({
    loginName: "",
    password: "",
    realName: "",
    userType: admin ? "2" : "2",
    customerId: defaultCustomerId
  }), [admin, defaultCustomerId]);

  const editRow = editModal.row;
  const editInitialValues = useMemo(() => ({
    loginName: editRow?.loginName || "",
    realName: editRow?.realName || "",
    userType: editRow == null ? "2" : String(editRow.userType),
    customerId: editRow?.customerId == null ? defaultCustomerId : String(editRow.customerId)
  }), [defaultCustomerId, editRow]);

  useEffect(() => {
    if (operator || !canView) return;
    loadCustomerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator, canView]);

  useEffect(() => {
    if (operator || !canView) return;
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator, canView, page]);

  async function loadCustomerOptions() {
    try {
      const res = await authFetch("/api/customers/options");
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "客户列表加载失败");
      }
      const data = await res.json();
      const nextCustomers = Array.isArray(data?.items) ? data.items : [];
      setCustomers(nextCustomers);
      setCustomerError("");

      if (!admin) {
        const ownCustomerId = defaultCustomerId || (nextCustomers[0]?.id == null ? "" : String(nextCustomers[0].id));
        setCustomerId(ownCustomerId);
      }
    } catch (err) {
      setCustomers([]);
      setCustomerError(err?.message || "客户列表加载失败");
    }
  }

  async function loadUsers(resetMsg = false) {
    setLoading(true);
    setError("");
    if (resetMsg) setMessage("");

    try {
      const qs = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize)
      });
      if (loginName.trim()) qs.set("loginName", loginName.trim());
      if (realName.trim()) qs.set("realName", realName.trim());
      if (userType !== "") qs.set("userType", userType);
      if (customerId.trim()) qs.set("customerId", customerId.trim());

      const res = await authFetch(`/api/users?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "用户查询失败");
      }
      const data = await res.json();
      setItems(data?.items || []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setError(err?.message || "用户查询失败");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    setTimeout(() => loadUsers(true), 0);
  }

  function openCreateModal() {
    setCreateModal({ open: true });
  }

  function openEditModal(row) {
    if (!canUpdate) return;
    setEditModal({ open: true, row });
  }

  function openResetPasswordModal(row) {
    if (!canPasswordReset) return;
    setPasswordModal({ open: true, row, newPassword: "" });
  }

  function openDeleteModal(row) {
    if (!canDelete) return;
    setDeleteModal({ open: true, row });
  }

  function buildUserPayload(form, includePassword) {
    const nextCustomerId = toNullableInt(form.customerId);
    const nextUserType = Number(form.userType);
    return {
      loginName: (form.loginName || "").trim(),
      password: includePassword ? form.password || "" : undefined,
      realName: (form.realName || "").trim() || null,
      userType: nextUserType,
      customerId: nextCustomerId
    };
  }

  function validateUserPayload(payload, includePassword) {
    if (!payload.loginName) return "账号不能为空。";
    if (includePassword && !String(payload.password || "").trim()) return "密码不能为空。";
    if (![0, 1, 2].includes(payload.userType)) return "角色无效。";
    if (!admin && payload.userType === 0) return "客户管理员不能创建或编辑 Admin。";
    if (!admin && !payload.customerId) return "客户不能为空。";
    return "";
  }

  async function submitCreateUser(form) {
    const payload = buildUserPayload(form, true);
    const validationError = validateUserPayload(payload, true);
    if (validationError) {
      setError(validationError);
      return;
    }

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "创建用户失败");
        return;
      }

      setCreateModal({ open: false });
      setMessage(`用户 ${payload.loginName} 创建成功。`);
      if (page === 1) {
        await loadUsers();
      } else {
        setPage(1);
      }
    } finally {
      setModalSubmitting(false);
    }
  }

  async function submitEditUser(form) {
    const row = editModal.row;
    if (!row) return;

    const payload = buildUserPayload(form, false);
    const validationError = validateUserPayload(payload, false);
    if (validationError) {
      setError(validationError);
      return;
    }

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/users/${row.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "编辑用户失败");
        return;
      }

      setEditModal({ open: false, row: null });
      setMessage(`用户 ${payload.loginName} 更新成功。`);
      await loadUsers();
    } finally {
      setModalSubmitting(false);
    }
  }

  async function submitResetPassword(form) {
    const row = passwordModal.row;
    if (!row) return;

    const pwd = (form.newPassword || "").trim();
    if (!pwd) {
      setError("新密码不能为空。");
      return;
    }

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/users/${row.id}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword: pwd })
      });

      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "重置密码失败");
        return;
      }

      setPasswordModal({ open: false, row: null, newPassword: "" });
      setMessage(`用户 ${row.loginName} 密码重置成功。`);
    } finally {
      setModalSubmitting(false);
    }
  }

  async function submitDeleteUser() {
    const row = deleteModal.row;
    if (!row) return;

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/users/${row.id}`, { method: "DELETE" });
      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "删除用户失败");
        return;
      }

      setDeleteModal({ open: false, row: null });
      setMessage(`用户 ${row.loginName} 删除成功。`);
      await loadUsers();
    } finally {
      setModalSubmitting(false);
    }
  }

  if (operator || !canView) {
    return (
      <div className="container-fluid">
        <div className="status-banner warn mt-3">当前账号无用户管理权限。</div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm status-card mt-3">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <span>用户管理</span>
          {canCreate && (
            <button type="button" className="btn btn-light btn-sm" onClick={openCreateModal}>
              新增用户
            </button>
          )}
        </div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={onSearch}>
            <div className="col-md-3">
              <label className="form-label">账号</label>
              <input className="form-control" value={loginName} onChange={(e) => setLoginName(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">姓名</label>
              <input className="form-control" value={realName} onChange={(e) => setRealName(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label">角色</label>
              <select className="form-select" value={userType} onChange={(e) => setUserType(e.target.value)}>
                <option value="">全部</option>
                {admin && <option value="0">Admin</option>}
                <option value="1">CustomerAdmin</option>
                <option value="2">Operator</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">客户</label>
              <select className="form-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)} disabled={!admin}>
                {filterCustomerOptions.map((option) => (
                  <option key={option.value || "all"} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                {loading ? "查询中..." : "查询"}
              </button>
            </div>
          </form>

          {error && <div className="status-banner error mt-3">{error}</div>}
          {customerError && <div className="status-banner warn mt-3">客户列表加载失败：{customerError}</div>}
          {message && <div className="status-banner info mt-3">{message}</div>}

          <div className="table-responsive mt-3">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>账号</th>
                  <th>姓名</th>
                  <th>角色</th>
                  <th>客户</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={7}><div className="status-banner empty m-0">暂无用户数据</div></td>
                  </tr>
                )}
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.loginName}</td>
                    <td>{row.realName || "-"}</td>
                    <td>{roleName(row.userType)}</td>
                    <td>{row.customerName || (row.customerId ? `#${row.customerId}` : "-")}</td>
                    <td>{toLocalDateTimeText(row.createdAt)}</td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        {canUpdate && (
                          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openEditModal(row)}>
                            编辑
                          </button>
                        )}
                        {canPasswordReset && (
                          <button type="button" className="btn btn-outline-warning btn-sm" onClick={() => openResetPasswordModal(row)}>
                            重置密码
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => openDeleteModal(row)}>
                            删除
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              共 {total} 条，当前第 {page}/{pageCount} 页
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>上一页</button>
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={page >= pageCount || loading} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>下一页</button>
            </div>
          </div>
        </div>
      </div>

      <ActionModal
        open={createModal.open && canCreate}
        title="新增用户"
        fields={[
          { name: "loginName", label: "账号", required: true },
          { name: "password", label: "密码", type: "password", required: true },
          { name: "realName", label: "姓名" },
          { name: "userType", label: "角色", type: "select", options: roleOptions, required: true },
          { name: "customerId", label: "客户", type: "select", options: customerOptions, disabled: !admin, required: !admin }
        ]}
        initialValues={createInitialValues}
        confirmText="新增"
        submitting={modalSubmitting}
        onCancel={() => setCreateModal({ open: false })}
        onConfirm={submitCreateUser}
      />

      <ActionModal
        open={editModal.open && canUpdate}
        title="编辑用户"
        message={editRow ? `账号：${editRow.loginName}` : ""}
        fields={[
          { name: "loginName", label: "账号", required: true },
          { name: "realName", label: "姓名" },
          { name: "userType", label: "角色", type: "select", options: roleOptions, required: true },
          { name: "customerId", label: "客户", type: "select", options: customerOptions, disabled: !admin, required: !admin }
        ]}
        initialValues={editInitialValues}
        confirmText="保存"
        submitting={modalSubmitting}
        onCancel={() => setEditModal({ open: false, row: null })}
        onConfirm={submitEditUser}
      />

      <ActionModal
        open={passwordModal.open && canPasswordReset}
        title="重置用户密码"
        message={passwordModal.row ? `账号：${passwordModal.row.loginName}` : ""}
        fields={[
          { name: "newPassword", label: "新密码", type: "password", required: true }
        ]}
        initialValues={{ newPassword: passwordModal.newPassword }}
        submitting={modalSubmitting}
        onCancel={() => setPasswordModal({ open: false, row: null, newPassword: "" })}
        onConfirm={submitResetPassword}
        confirmClassName="btn-warning"
      />

      <ActionModal
        open={deleteModal.open && canDelete}
        title="删除用户"
        message={deleteModal.row ? `确认删除用户 ${deleteModal.row.loginName}？当前登录用户不能删除自己。` : ""}
        fields={[]}
        confirmText="删除"
        confirmClassName="btn-danger"
        submitting={modalSubmitting}
        onCancel={() => setDeleteModal({ open: false, row: null })}
        onConfirm={submitDeleteUser}
      />
    </div>
  );
}
